import { c as _c } from "react/compiler-runtime";
import { Button, IconButton, MobileMenu, useTranslationContext } from 'platform/components';
import { Heading } from 'platform/foundation-tw';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useOnMount } from 'shared/web';
import type { EagColumnGroup, EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import type { FilterState } from '../../../../types/FilterState';
import { isEagColumnGroup } from '../../../../utils/isEagColumnGroup';
import { FilterContentArea } from '../FilterContentArea/FilterContentArea';
type MobileFilterPanelProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
  isOpen: boolean;
  onClose: () => void;
};
type CategoryOption = {
  value: string;
  label: string;
};

// APP_HEADER_HEIGHT = 12 theme units = 48px = top-12 in Tailwind.
// Matches the DMS Layout grid-template-rows and APP_HEADER_HEIGHT in activity-panel.
const DRAWER_CLASS = 'fixed top-12 inset-x-0 bottom-0 bg-white flex flex-col overflow-hidden z-(--z-header-popper)';
const buildCategoryOptions = (columnConfig: EagColumnOrGroup[]): CategoryOption[] => {
  const seen = new Set<string>();
  const options: CategoryOption[] = [];
  let hasStandalone = false;
  for (const item of columnConfig) {
    if (isEagColumnGroup(item)) {
      const group = item as EagColumnGroup;
      if (!seen.has(group.groupId)) {
        seen.add(group.groupId);
        options.push({
          value: group.groupId,
          label: group.headerName
        });
      }
    } else {
      hasStandalone = true;
    }
  }
  if (hasStandalone) {
    options.push({
      value: '__standalone__',
      label: 'Other'
    });
  }
  return options;
};

/**
 * Mobile filter drawer — renders as a portal over the entire app viewport.
 *
 * Portal target: `document.body`
 * Position: `fixed top-12 (48px = app header height) inset-x-0 bottom-0`
 *
 * Layout:
 * ```
 * ┌─────────────────────────────────┐  ← top: 48px (app header bottom)
 * │ Filters (Heading size=4)      ✕ │  ← PINNED header (border-b)
 * ├─────────────────────────────────┤
 * │ [MobileMenu: category ▼]        │  ← SCROLLABLE body (not pinned)
 * │  filter widgets grid...         │
 * ├─────────────────────────────────┤
 * │ [Reset filters]                 │  ← PINNED footer (border-t separator)
 * └─────────────────────────────────┘  ← bottom: 0
 * ```
 */
export function MobileFilterPanel(props) {
  const $ = _c(31);
  const t = useTranslationContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  let t0;
  if ($[0] !== props.columnConfig) {
    t0 = buildCategoryOptions(props.columnConfig);
    $[0] = props.columnConfig;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const categoryOptions = t0;
  let t1;
  if ($[2] !== categoryOptions[0] || $[3] !== categoryOptions.length) {
    t1 = () => {
      if (categoryOptions.length > 0) {
        setSelectedCategoryId(categoryOptions[0].value);
      }
    };
    $[2] = categoryOptions[0];
    $[3] = categoryOptions.length;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useOnMount(t1);
  if (!props.isOpen) {
    return null;
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("page.datagrid.labels.filters");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <Heading size={4}>{t2}</Heading>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props.onClose) {
    t4 = <IconButton size="small" icon="navigation/close" onClick={props.onClose} />;
    $[9] = props.onClose;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== categoryOptions || $[15] !== selectedCategoryId) {
    t6 = <div className="px-4 py-3 border-b border-gray-100"><MobileMenu options={categoryOptions} value={selectedCategoryId} placeholder="Select category" onClick={setSelectedCategoryId} /></div>;
    $[14] = categoryOptions;
    $[15] = selectedCategoryId;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== props.columnConfig || $[18] !== props.filterState || $[19] !== props.onFilterChange || $[20] !== selectedCategoryId) {
    t7 = <FilterContentArea columnConfig={props.columnConfig} selectedCategoryId={selectedCategoryId} filterState={props.filterState} onFilterChange={props.onFilterChange} searchTerm="" noScroll={true} />;
    $[17] = props.columnConfig;
    $[18] = props.filterState;
    $[19] = props.onFilterChange;
    $[20] = selectedCategoryId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <div className="flex-1 overflow-y-auto">{t6}{t7}</div>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== props.onResetFilters) {
    t9 = <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200"><Button variant="secondary" isFullWidth={true} onClick={props.onResetFilters} title="Reset filters" /></div>;
    $[25] = props.onResetFilters;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t8 || $[29] !== t9) {
    t10 = createPortal(<div className={DRAWER_CLASS}>{t5}{t8}{t9}</div>, document.body);
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}